import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useToast } from '../../../../context/ToastContext';
import apiService from '../../../../services/apiService';
import { Button } from '../../../../components/ui';
import { FaEdit, FaArrowLeft } from 'react-icons/fa';

const breadcrumb = (
  <span>
    <span className="cursor-pointer hover:text-blue-600" onClick={() => window.history.back()}>
      Home
    </span> &nbsp;/&nbsp; 
    <span className="cursor-pointer hover:text-blue-600" onClick={() => window.history.back()}>
      Education Standards
    </span> &nbsp;/&nbsp; 
    <span className="text-gray-700">View</span>
  </span>
);

const ViewEduStandard = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { showToast } = useToast();
  
  const [eduStandard, setEduStandard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('basic');

  useEffect(() => {
    const fetchEduStandard = async () => {
      try {
        setLoading(true);
        const data = await apiService.request(`/i/ims/edustandard/${id}`);
        setEduStandard(data);
      } catch (error) {
        console.error('Error fetching education standard:', error);
        showToast('error', 'Failed to fetch education standard details');
        navigate('/course-programs/edu-standards');
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchEduStandard();
    }
  }, [id, navigate, showToast]);

  const handleEdit = () => {
    navigate(`/course-programs/edu-standards/edit/${id}`);
  };

  const handleBack = () => {
    navigate('/course-programs/edu-standards');
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="bg-white shadow rounded-lg">
      <div className="border-b border-gray-200">
        <div className="p-6">
          <nav className="text-xs text-gray-500 mb-4" aria-label="Breadcrumb">
            {breadcrumb}
          </nav>
          <div className="flex justify-between items-center mb-4">
            <h1 className="text-2xl font-bold text-gray-900">{eduStandard?.name || 'Education Standard'}</h1>
            <div className="flex space-x-2">
              <Button
                onClick={handleBack}
                variant="outline"
                size="sm"
                className="border-gray-300 text-gray-700"
                icon={FaArrowLeft}
              >
                Back
              </Button>
              <Button
                onClick={handleEdit}
                variant="outline"
                size="sm"
                className="border-amber-600 text-amber-600 hover:bg-amber-50"
                icon={FaEdit}
              >
                Edit
              </Button>
            </div>
          </div>
          
          {/* Tabs */}
          <div className="border-b border-gray-200">
            <nav className="flex -mb-px space-x-8">
              <button
                onClick={() => setActiveTab('basic')}
                className={`py-4 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'basic'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Basic Details
              </button>
              <button
                onClick={() => setActiveTab('meta')}
                className={`py-4 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'meta'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Meta Tags
              </button>
            </nav>
          </div>
        </div>
        
        <div className="p-6">
          {activeTab === 'basic' && eduStandard && (
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h3 className="text-sm font-medium text-gray-500">Name</h3>
                  <p className="mt-1 text-sm text-gray-900">{eduStandard.name || '-'}</p>
                </div>
                
                <div>
                  <h3 className="text-sm font-medium text-gray-500">Code</h3>
                  <p className="mt-1 text-sm text-gray-900">{eduStandard.code || '-'}</p>
                </div>
                
                <div>
                  <h3 className="text-sm font-medium text-gray-500">URI</h3>
                  <p className="mt-1 text-sm text-gray-900">{eduStandard.uri || '-'}</p>
                </div>
                
                <div>
                  <h3 className="text-sm font-medium text-gray-500">Order</h3>
                  <p className="mt-1 text-sm text-gray-900">{eduStandard.order || '0'}</p>
                </div>
                
                <div>
                  <h3 className="text-sm font-medium text-gray-500">Status</h3>
                  <p className="mt-1">
                    <span className={`px-2 py-1 rounded-full text-xs ${
                      eduStandard.status 
                        ? 'bg-green-100 text-green-800' 
                        : 'bg-red-100 text-red-800'
                    }`}>
                      {eduStandard.status ? 'Active' : 'Inactive'}
                    </span>
                  </p>
                </div>
                
                <div>
                  <h3 className="text-sm font-medium text-gray-500">Thumbnail</h3>
                  {eduStandard.thumb ? (
                    <div className="mt-1">
                      <img 
                        src={eduStandard.thumb} 
                        alt={eduStandard.name} 
                        className="h-20 w-auto object-contain border rounded"
                      />
                    </div>
                  ) : (
                    <p className="mt-1 text-sm text-gray-500">No thumbnail</p>
                  )}
                </div>
              </div>
              
              <div className="mt-6">
                <h3 className="text-sm font-medium text-gray-500">Created At</h3>
                <p className="mt-1 text-sm text-gray-900">
                  {eduStandard.createdAt ? new Date(eduStandard.createdAt).toLocaleString() : '-'}
                </p>
              </div>
              
              <div>
                <h3 className="text-sm font-medium text-gray-500">Updated At</h3>
                <p className="mt-1 text-sm text-gray-900">
                  {eduStandard.updatedAt ? new Date(eduStandard.updatedAt).toLocaleString() : '-'}
                </p>
              </div>
            </div>
          )}
          
          {activeTab === 'meta' && eduStandard && (
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h3 className="text-sm font-medium text-gray-500">OG Title</h3>
                  <p className="mt-1 text-sm text-gray-900">{eduStandard.metaTags?.ogTitle || '-'}</p>
                </div>
                
                <div>
                  <h3 className="text-sm font-medium text-gray-500">Keywords</h3>
                  <p className="mt-1 text-sm text-gray-900">{eduStandard.metaTags?.keywords || '-'}</p>
                </div>
                
                <div className="col-span-2">
                  <h3 className="text-sm font-medium text-gray-500">OG Description</h3>
                  <p className="mt-1 text-sm text-gray-900">{eduStandard.metaTags?.ogDescription || '-'}</p>
                </div>
                
                <div className="col-span-2">
                  <h3 className="text-sm font-medium text-gray-500">OG Image</h3>
                  {eduStandard.metaTags?.ogImage ? (
                    <div className="mt-1">
                      <img 
                        src={eduStandard.metaTags.ogImage} 
                        alt="OG Image" 
                        className="h-32 w-auto object-contain border rounded"
                      />
                    </div>
                  ) : (
                    <p className="mt-1 text-sm text-gray-500">No OG image</p>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ViewEduStandard;